import { useOrganizationContent } from '@cibo/profile'
import { logRequestError } from '@cibo/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AgreementsAPI } from '../api'
import { AgreementModel } from '../models'
import { AgreementHistory, AgreementListRequest, AgreementNote, AgreementShape } from '../types'
import { AGREEMENTS_QUERY_KEY } from './queryKey'

export const useAgreements = (requestParams?: AgreementListRequest, includeStats?: boolean) => {
  return useQuery([AGREEMENTS_QUERY_KEY.AGREEMENTS, requestParams], async () => {
    const agreements = await AgreementsAPI.getAgreements(requestParams)
    if (includeStats) {
      //query for _each_ agreement's rollup. sigh
      const rollups = await Promise.all(
        agreements.items.map(async agreement =>
          AgreementsAPI.getAgreement({
            agreementId: agreement.agreementId,
            returnStats: true,
          }).then(response => ({
            ...response,
            ...agreement,
          }))
        )
      )

      return {
        ...agreements,
        items: rollups.map(a => new AgreementModel(a)),
      }
    }
    return {
      ...agreements,
      items: agreements.items.map(a => new AgreementModel(a)),
    }
  })
}

export const useAgreement = (id?: string) => {
  const orgContent = useOrganizationContent()
  return useQuery({
    //@ts-ignore queryKey is mistyped as only being allowed on the generic useQuery type
    queryKey: [AGREEMENTS_QUERY_KEY.AGREEMENTS, id],
    queryFn: async () => {
      if (!id) throw new Error('useAgreement requires an id')
      const agreement = await AgreementsAPI.getAgreement({ agreementId: id })

      const model = new AgreementModel(agreement)

      model.setAgreementCopy(orgContent.data?.fields.agreementCopy)
      return model
    },
    enabled: !!id && orgContent.isFetched,
  })
}

export const useAgreementNotes = (id: string) => {
  return useQuery<AgreementNote[]>(
    [AGREEMENTS_QUERY_KEY.AGREEMENT_NOTES, id],
    async () => await AgreementsAPI.getAgreementNotes(id)
  )
}

export const useAgreementHistory = (id: string) => {
  return useQuery<AgreementHistory[]>(
    [AGREEMENTS_QUERY_KEY.AGREEMENT_HISTORY, id],
    async () => await AgreementsAPI.getAgreementHistory(id)
  )
}

export const useUpdateAgreement = () => {
  const queryClient = useQueryClient()

  return useMutation(AgreementsAPI.updateAgreement, {
    onSuccess: (response: AgreementShape) => {
      queryClient.invalidateQueries([AGREEMENTS_QUERY_KEY.AGREEMENTS])
      queryClient.invalidateQueries([AGREEMENTS_QUERY_KEY.AGREEMENTS, response.agreementId])
      queryClient.invalidateQueries([AGREEMENTS_QUERY_KEY.AGREEMENT_NOTES, response.agreementId])
      queryClient.invalidateQueries([AGREEMENTS_QUERY_KEY.AGREEMENT_HISTORY, response.agreementId])
    },
    onError: logRequestError,
  })
}
